const root = document.querySelector('[data-kv-quick-add]');

if (root && !window.kalvantisQuickAddReady) {
  window.kalvantisQuickAddReady = true;

  const drawer = root.querySelector('.kalvantis-quick-add__drawer');
  const image = root.querySelector('[data-kv-quick-add-image]');
  const title = root.querySelector('[data-kv-quick-add-title]');
  const price = root.querySelector('[data-kv-quick-add-price]');
  const fabric = root.querySelector('[data-kv-quick-add-fabric]');
  const optionsTarget = root.querySelector('[data-kv-quick-add-options]');
  const stock = root.querySelector('[data-kv-quick-add-stock]');
  const quantityInput = root.querySelector('[data-kv-quick-add-quantity]');
  const submit = root.querySelector('[data-kv-quick-add-submit]');
  const submitText = root.querySelector('[data-kv-submit-text]');
  const toast = root.querySelector('[data-kv-quick-add-toast]');

  let product = null;
  let selectedOptions = [];
  let selectedVariant = null;
  let previousFocus = null;
  let toastTimer = null;

  const colorMap = {
    black: 'hsl(152 30% 12%)',
    charcoal: 'hsl(152 8% 34%)',
    grey: 'hsl(152 8% 52%)',
    gray: 'hsl(152 8% 52%)',
    white: 'hsl(0 0% 99%)',
    navy: 'hsl(152 48% 14%)',
    blue: 'hsl(152 42% 22%)',
    royal: 'hsl(152 42% 22%)',
    ceil: 'hsl(152 28% 95%)',
    green: 'hsl(152 42% 22%)',
    forest: 'hsl(152 42% 22%)',
    olive: 'hsl(152 42% 22%)',
    burgundy: 'hsl(43 72% 38%)',
    wine: 'hsl(43 72% 38%)',
    gold: 'hsl(43 68% 53%)'
  };

  const normalize = (value) => String(value || '').toLowerCase();
  const getVariantOptions = (variant) => variant?.options || [variant?.option1, variant?.option2, variant?.option3].filter(Boolean);
  const isColorOption = (option) => /colou?r/i.test(option?.name || '');

  const findVariant = () => {
    if (!product) return null;
    return product.variants.find((variant) => {
      const options = getVariantOptions(variant);
      return selectedOptions.every((value, index) => options[index] === value);
    }) || null;
  };

  const isOptionValueAvailable = (optionIndex, value) => {
    if (!product) return false;
    const test = [...selectedOptions];
    test[optionIndex] = value;
    return product.variants.some((variant) => {
      if (!variant.available) return false;
      const options = getVariantOptions(variant);
      return test.every((optionValue, index) => !optionValue || options[index] === optionValue);
    });
  };

  const swatchColor = (value) => {
    const label = normalize(value);
    const key = Object.keys(colorMap).find((name) => label.includes(name));
    return colorMap[key] || 'hsl(152 28% 95%)';
  };

  const renderOptions = () => {
    optionsTarget.replaceChildren();
    product.options.forEach((option, optionIndex) => {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'kalvantis-quick-add__option';

      const legend = document.createElement('legend');
      legend.textContent = option.name;
      fieldset.append(legend);

      const values = document.createElement('div');
      values.className = 'kalvantis-quick-add__values';

      option.values.forEach((value) => {
        const available = isOptionValueAvailable(optionIndex, value);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kalvantis-quick-add__value';
        button.textContent = isColorOption(option) ? '' : value;
        button.dataset.optionIndex = String(optionIndex);
        button.dataset.optionValue = value;
        button.setAttribute('aria-pressed', String(selectedOptions[optionIndex] === value));

        if (isColorOption(option)) {
          button.classList.add('kalvantis-quick-add__value--swatch');
          button.style.setProperty('--kv-swatch', swatchColor(value));
          button.setAttribute('aria-label', value);
          button.title = value;
        }

        if (selectedOptions[optionIndex] === value) button.classList.add('is-selected');
        if (!available) {
          button.classList.add('is-disabled');
          button.disabled = true;
          button.title = 'Out of Stock';
          button.setAttribute('aria-label', `${value} - Out of Stock`);
        }

        values.append(button);
      });

      fieldset.append(values);
      optionsTarget.append(fieldset);
    });
  };

  const updateState = () => {
    selectedVariant = findVariant();
    const isAvailable = Boolean(selectedVariant?.available);
    price.textContent = selectedVariant?.price || product.price;
    stock.textContent = isAvailable ? 'In stock' : 'Out of stock';
    submit.disabled = !isAvailable;
    submitText.textContent = isAvailable ? 'Add to cart' : 'Select available options';
    renderOptions();
  };

  const open = (data, trigger) => {
    product = data;
    previousFocus = trigger || document.activeElement;
    const initialVariant = product.variants.find((variant) => variant.id === product.selectedVariantId) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];

    selectedOptions = [...getVariantOptions(initialVariant)];
    selectedVariant = initialVariant;
    quantityInput.value = '1';

    title.textContent = product.title;
    price.textContent = initialVariant?.price || product.price;
    fabric.textContent = product.fabric || 'Premium performance fabric tailored for clinical movement.';
    image.src = product.featuredImage || product.secondaryImage || '';
    image.alt = product.title;

    root.hidden = false;
    requestAnimationFrame(() => root.classList.add('is-open'));
    document.documentElement.style.overflow = 'hidden';
    updateState();
    drawer.focus();
  };

  const close = () => {
    root.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    window.setTimeout(() => {
      root.hidden = true;
      if (previousFocus && document.contains(previousFocus)) previousFocus.focus();
    }, 280);
  };

  const showToast = (message) => {
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const refreshCartDrawer = async () => {
    const url = new URL(window.location.origin + window.location.pathname);
    url.searchParams.set('sections', 'cart-drawer-section');
    url.searchParams.set('_kv', String(Date.now()));
    const response = await fetch(url.toString());
    if (!response.ok) return;
    const sections = await response.json();
    const html = sections['cart-drawer-section'];
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('#shopify-section-cart-drawer-section');
    const current = document.querySelector('#shopify-section-cart-drawer-section');
    if (next && current) current.replaceWith(next);
  };

  const fetchCart = async () => {
    const cartUrl = `${window.Theme?.routes?.cart_url || '/cart'}.json`;
    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to verify cart.');
    return response.json();
  };

  const syncCartBubble = async (count) => {
    const cartIcon = document.querySelector('cart-icon');
    if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
      await cartIcon.renderCartBubble(count, false);
    }
  };

  const openCartDrawer = () => {
    const cartDrawer = document.querySelector('theme-drawer#cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    document.querySelector('[aria-controls="cart-drawer"]')?.click();
  };

  const addToCart = async () => {
    if (!selectedVariant?.available || submit.classList.contains('is-loading')) return;

    submit.classList.add('is-loading');
    submit.disabled = true;

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: selectedVariant.id,
          quantity: Math.max(1, Number(quantityInput.value) || 1)
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Unable to add this item.');
      }

      const cart = await fetchCart();
      if (!Number(cart?.item_count || 0)) {
        throw new Error('Item was not added to cart.');
      }

      await syncCartBubble(Number(cart.item_count || 0));
      await refreshCartDrawer();
      close();
      openCartDrawer();
      showToast('Added to cart');
    } catch (error) {
      showToast(error.message || 'Unable to add this item.');
      updateState();
    } finally {
      submit.classList.remove('is-loading');
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-kv-quick-add-trigger]');
    if (trigger) {
      const card = trigger.closest('[data-kv-product-card]');
      const json = card?.querySelector('[data-kv-product-json]');
      if (!json) return;
      event.preventDefault();
      open(JSON.parse(json.textContent), trigger);
      return;
    }

    if (event.target.closest('[data-kv-quick-add-close]')) {
      close();
      return;
    }

    const option = event.target.closest('.kalvantis-quick-add__value');
    if (option && !option.disabled) {
      selectedOptions[Number(option.dataset.optionIndex)] = option.dataset.optionValue;
      updateState();
      return;
    }

    if (event.target.closest('[data-kv-quantity-minus]')) {
      quantityInput.value = String(Math.max(1, Number(quantityInput.value) - 1 || 1));
      return;
    }

    if (event.target.closest('[data-kv-quantity-plus]')) {
      quantityInput.value = String(Math.max(1, Number(quantityInput.value) + 1 || 2));
      return;
    }

    if (event.target.closest('[data-kv-quick-add-submit]')) addToCart();
  });

  document.addEventListener('keydown', (event) => {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = [...drawer.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}
